import {
  perspectiveInputSchema,
  perspectiveListSchema,
  perspectiveSchema,
  perspectiveWeightTotal,
  type Perspective,
  type PerspectiveInput,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  FolderKanban,
  Lock,
  Layers,
  Pencil,
  Plus,
} from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

/** Cor de cada perspectiva pela posição (paleta categórica, sem significado de status). */
const SERIES = ['bg-series-1', 'bg-series-2', 'bg-series-3', 'bg-series-4', 'bg-series-5', 'bg-series-6'];
const seriesOf = (index: number) => SERIES[index % SERIES.length]!;

const formatWeight = (w: number) => `${w.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/** Estratégia › Perspectivas (T-040, RF-15): as perspectivas do BSC do projeto, na ordem, com pesos. */
export function PerspectivasPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-4xl">
      <PageHeader
        title="Perspectivas"
        description="As perspectivas organizam os objetivos do BSC. A ordem é a do mapa estratégico e o peso define quanto cada uma conta na performance da empresa."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="As perspectivas pertencem a um projeto. Quando a empresa tiver um projeto em andamento, elas aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <PerspectivesSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function PerspectivesSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  /** Projeto arquivado: só consulta. */
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Perspective | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Perspective | null>(null);
  const base = `/organizations/${organizationId}/projects/${projectId}/perspectives`;
  const key = ['perspectives', projectId, showArchived];

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(base, {
        query: { archived: showArchived ? 'true' : undefined },
        schema: perspectiveListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['perspectives', projectId] });

  const move = useMutation({
    meta: { handlesError: true },
    mutationFn: (ids: string[]) =>
      client.request(`${base}/order`, { method: 'PUT', body: { ids }, schema: perspectiveListSchema }),
    onSuccess: () => refresh(),
    onError: async (err) => {
      toast.error('Não foi possível reordenar', { description: errorMessage(err) });
      await refresh();
    },
  });

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (p: Perspective) =>
      client.request(`${base}/${p.id}/${p.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: perspectiveSchema,
      }),
    onSuccess: async (p) => {
      setArchiving(null);
      toast.success(p.archivedAt ? `Perspectiva ${p.name} arquivada` : `Perspectiva ${p.name} restaurada`);
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: PerspectiveInput) {
    const target = editing === 'new' ? null : editing;
    const saved = await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PATCH' : 'POST',
      body: input,
      schema: perspectiveSchema,
    });
    setEditing(null);
    toast.success(target ? 'Perspectiva salva' : `Perspectiva ${saved.name} criada`);
    await refresh();
  }

  if (query.isPending) return <LoadingState className="mt-6" rows={4} label="Carregando as perspectivas" />;
  if (query.isError) {
    return (
      <ErrorState
        className="mt-6"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }

  const items = query.data.items;
  const active = items.filter((p) => !p.archivedAt);
  const archived = items.filter((p) => p.archivedAt);
  const total = perspectiveWeightTotal(items);
  const withWeight = active.filter((p) => p.weight !== null);
  const canEdit = can('planejamento-estrategico', 'edit');

  const reorder = (index: number, delta: -1 | 1) => {
    const ids = active.map((p) => p.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + delta, 0, moved!);
    // Mostra a nova ordem na hora; a API confirma (ou a lista volta ao que está salvo).
    queryClient.setQueryData(key, {
      items: [...ids.map((id, position) => ({ ...active.find((p) => p.id === id)!, position })), ...archived],
    });
    move.mutate(ids);
  };

  return (
    <div className="mt-6 flex flex-col gap-6">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: as perspectivas ficam disponíveis só para consulta.
        </p>
      ) : null}
      <section aria-label="Distribuição dos pesos" className="rounded-lg border border-border bg-surface p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-bold">Pesos na performance da empresa</h2>
          <p
            className={cn(
              'text-sm font-semibold',
              total === 100 ? 'text-status-green-fg' : 'text-status-yellow-fg',
            )}
          >
            Soma: {formatWeight(total)}
            <span className="font-normal">
              {total === 100
                ? ' — tudo certo'
                : withWeight.length
                  ? ' — o ideal é somar 100%'
                  : ' — nenhuma com peso'}
            </span>
          </p>
        </div>
        <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-muted" aria-hidden>
          {active.map((p, i) =>
            p.weight ? (
              <div
                key={p.id}
                className={cn(seriesOf(i), 'h-full border-r-2 border-surface last:border-r-0')}
                style={{ width: `${(p.weight / Math.max(total, 100)) * 100}%` }}
              />
            ) : null,
          )}
        </div>
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
          {active.map((p, i) => (
            <li key={p.id} className="flex items-center gap-1.5">
              <span className={cn('size-2.5 rounded-full', seriesOf(i))} aria-hidden />
              {p.name}
              <span className="text-muted-foreground">
                {p.weight === null ? 'sem peso' : formatWeight(p.weight)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section aria-label="Perspectivas do projeto">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-bold">
            Perspectivas <span className="font-normal text-muted-foreground">({active.length})</span>
          </h2>
          {can('planejamento-estrategico', 'create') ? (
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus aria-hidden />
              Nova perspectiva
            </Button>
          ) : null}
        </div>
        <ol
          aria-label="Ordem das perspectivas"
          aria-busy={move.isPending || query.isFetching}
          className="flex flex-col gap-2"
        >
          {active.map((p, i) => (
            <li
              key={p.id}
              className="flex items-stretch overflow-hidden rounded-lg border border-border bg-surface"
            >
              <span className={cn('w-1.5 shrink-0', seriesOf(i))} aria-hidden />
              {/* Celular: nome e descrição na largura toda; peso e ações na linha de baixo. */}
              <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{p.name}</p>
                    {p.description ? (
                      <p className="mt-0.5 text-sm text-muted-foreground">{p.description}</p>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 pl-11 sm:justify-end sm:pl-0">
                  <Badge variant={p.weight === null ? 'outline' : 'neutral'}>
                    {p.weight === null ? 'Sem peso' : `Peso ${formatWeight(p.weight)}`}
                  </Badge>
                  <div className="flex gap-1">
                    {canEdit ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Subir ${p.name}`}
                          disabled={i === 0 || move.isPending}
                          onClick={() => reorder(i, -1)}
                        >
                          <ArrowUp aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Descer ${p.name}`}
                          disabled={i === active.length - 1 || move.isPending}
                          onClick={() => reorder(i, 1)}
                        >
                          <ArrowDown aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Editar ${p.name}`}
                          onClick={() => setEditing(p)}
                        >
                          <Pencil aria-hidden />
                        </Button>
                      </>
                    ) : null}
                    {can('planejamento-estrategico', 'delete') ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Arquivar ${p.name}`}
                        disabled={active.length === 1}
                        title={
                          active.length === 1
                            ? 'O projeto precisa de pelo menos uma perspectiva ativa.'
                            : undefined
                        }
                        onClick={() => setArchiving(p)}
                      >
                        <Archive aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ol>
        <label className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Mostrar arquivadas
        </label>
        {showArchived ? (
          archived.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nenhuma perspectiva arquivada.</p>
          ) : (
            <ul aria-label="Perspectivas arquivadas" className="mt-2 flex flex-col gap-2">
              {archived.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-muted-foreground">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Arquivada — o histórico continua guardado.
                    </p>
                  </div>
                  {can('planejamento-estrategico', 'delete') ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={toggleArchive.isPending}
                      onClick={() => toggleArchive.mutate(p)}
                    >
                      <ArchiveRestore aria-hidden />
                      Restaurar
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )
        ) : null}
        {active.length === 0 ? (
          <EmptyState
            className="py-8"
            icon={Layers}
            title="Nenhuma perspectiva ativa"
            description="Restaure uma perspectiva arquivada ou crie uma nova."
          />
        ) : null}
      </section>

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Nova perspectiva' : 'Editar perspectiva'}
      >
        {editing !== null ? (
          <PerspectiveForm
            initial={editing === 'new' ? undefined : editing}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar a perspectiva?"
        description={`${archiving?.name ?? ''} sai do BSC ativo e do mapa. O histórico fica guardado e você pode restaurá-la depois.`}
        confirmLabel="Arquivar perspectiva"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </div>
  );
}

function PerspectiveForm({
  initial,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: Perspective;
  onSubmit: (input: PerspectiveInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [weight, setWeight] = useState(
    initial?.weight === null || initial?.weight === undefined ? '' : String(initial.weight),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), description: useId(), weight: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = perspectiveInputSchema.safeParse({
      name,
      description,
      weight: weight.trim() === '' ? null : weight.replace(',', '.'),
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      // Descrição esvaziada vai como null: o PATCH só muda os campos enviados.
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={80}
          placeholder="Ex.: Sustentabilidade"
          onChange={(e) => setName(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          maxLength={500}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid"
        />
      </FormField>
      <FormField
        id={ids.weight}
        label="Peso na performance (%)"
        error={errors.weight}
        hint={
          <p className="text-xs text-muted-foreground">
            De 0 a 100. Deixe em branco para não entrar na ponderação.
          </p>
        }
      >
        <Input
          {...fieldA11y(ids.weight, errors.weight, true)}
          value={weight}
          inputMode="decimal"
          maxLength={6}
          onChange={(e) => setWeight(e.target.value)}
          className="h-11 w-32"
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar perspectiva' : 'Criar perspectiva'}
        </Button>
      </div>
    </form>
  );
}
